import { useQuery } from "@apollo/client";
import { usePagination } from "@mantine/hooks";
import clsx from "clsx";
import { format } from "date-fns";
import { ChangeEvent, useState } from "react";
import { OrderDirection, QueryMode } from "../../__generated__/graphql";
import { GetObjectItems } from "../../api/object-items";
import { GetObjectMaterials } from "../../api/object-materials";
import { GetObjectPublications } from "../../api/object-publications";
import { GetObjectTechniques } from "../../api/object-techniques";
import { GetObjects } from "../../api/objects";
import { GetPeople } from "../../api/people";
import { GetPlaceProperties, GetPlaces } from "../../api/places";
import Button, { ButtonLayout } from "../../components/core/Button/Button";
import Center from "../../components/core/Center/Center";
import Loader from "../../components/core/Loader/Loader";
import Pagination from "../../components/core/Pagination/Pagination";
import Range from "../../components/core/Range/Range";
import Text from "../../components/core/Text/Text";
import Checkbox from "../../components/form/Checkbox/Checkbox";
import Search from "../../components/form/Search/Search";
import { translate } from "../../localization";
import { parseDate } from "../../utils/date";
import styles from "./Browse.module.scss";
import Image from "./Image";
import List from "./List";
import Timeline from "./Timeline";

export enum FilterCategory {
  None = "",
  People = "people",
  Places = "places",
  Objects = "objects",
  Groups = "groups",
  Events = "events",
}

enum FilterObjectCategory {
  Publications = "clo7w2i100vmkpz7lnrjuigjs",
  ArtWorks = "clo7w2i0i0vm6pz7l5ua5w9oz|clo7w2i0o0vm8pz7lxg2q8x18|clo7w2i0t0vmgpz7l44g97wlz|clo7w2hzx0vm4pz7lfqtnlpi7",
  Items = "cloqxgx27000smf7lxkxtlono|cloqztuvm002gmf7lidphusab|clo7w2i0s0vmepz7l7d3ix3cr",
}

enum ResultView {
  Timeline = "timeline",
  List = "list",
  Image = "image",
}

const Browse = () => {
  const pageSize = 80;
  const i18n = translate();
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [filter, setFilter] = useState<string[]>([]);
  const [category, setCategory] = useState(FilterCategory.People);
  const [resultView, setResultView] = useState(ResultView.List);
  const [objectPublicationsFilter, setObjectPublicationsFilter] = useState<
    string[]
  >([]);
  const [years, setYears] = useState<number[]>([1700, 2000]);
  const [objectItemsFilter, setObjectItemsFilter] = useState<string[]>([]);
  const [objectMaterialFilter, setObjectMaterialFilter] = useState<string[]>(
    [],
  );
  const [objectTechniqueFilter, setObjectTechniqueFilter] = useState<string[]>(
    [],
  );
  const [objectCategory, setObjectCategory] = useState(
    FilterObjectCategory.Publications,
  );

  const { data: people, loading: peoplesLoading } = useQuery(GetPeople, {
    skip: category !== FilterCategory.People,
    variables: {
      orderBy: {
        dateOfBirth: OrderDirection.Asc,
      },
      take: pageSize,
      skip: (page - 1) * pageSize,
      hydrateRelationships: true,
      where: {
        ...(searchTerm &&
          searchTerm.length > 0 && {
            OR: [
              {
                givenName: {
                  contains: searchTerm,
                  mode: QueryMode.Insensitive,
                },
              },
              {
                familyName: {
                  contains: searchTerm,
                  mode: QueryMode.Insensitive,
                },
              },
              {
                fullName: {
                  contains: searchTerm,
                  mode: QueryMode.Insensitive,
                },
              },
            ],
          }),
        dateOfBirth: {
          gte: `${years[0]}-01-01`,
          lte: `${years[1]}-12-31`,
        },
      },
    },
  });

  const { data: objects, loading: objectsLoading } = useQuery(GetObjects, {
    skip: category !== FilterCategory.Objects,
    variables: {
      take: pageSize,
      skip: (page - 1) * pageSize,
      hydrateRelationships: true,
      where: {
        ...(searchTerm &&
          searchTerm.length > 0 && {
            OR: [
              {
                title: {
                  contains: searchTerm,
                  mode: QueryMode.Insensitive,
                },
              },
            ],
          }),
        ...(objectCategory.length > 0 && {
          typ: {
            some: {
              id: {
                in: objectCategory.split("|"),
              },
            },
          },
        }),
        ...(objectPublicationsFilter.length > 0 && {
          eigenschaftenPublikation: {
            some: {
              id: {
                in: objectPublicationsFilter,
              },
            },
          },
        }),
        ...(objectItemsFilter.length > 0 && {
          eigenschaftenVolkskunde: {
            some: {
              id: {
                in: objectItemsFilter,
              },
            },
          },
        }),
        ...(objectMaterialFilter.length > 0 && {
          eigenschaftenMaterial: {
            some: {
              id: {
                in: objectMaterialFilter,
              },
            },
          },
        }),
        ...(objectTechniqueFilter.length > 0 && {
          eigenschaftenTechnik: {
            some: {
              id: {
                in: objectTechniqueFilter,
              },
            },
          },
        }),
        periodOfOrigin: {
          zeitspanne: {
            von: {
              gte: `${years[0]}-01-01`,
              lte: `${years[1]}-12-31`,
            },
            bis: {
              gte: `${years[0]}-01-01`,
              lte: `${years[1]}-12-31`,
            },
          },
        },
      },
    },
  });

  const { data: places, loading: placesLoading } = useQuery(GetPlaces, {
    skip: category !== FilterCategory.Places,
    variables: {
      take: pageSize,
      skip: (page - 1) * pageSize,
      hydrateRelationships: true,
      where: {
        ...(searchTerm &&
          searchTerm.length > 0 && {
            OR: [
              {
                name: {
                  contains: searchTerm,
                  mode: QueryMode.Insensitive,
                },
              },
            ],
          }),
        ...(filter.length > 0 && {
          eigenschaften: {
            some: {
              id: {
                in: filter,
              },
            },
          },
        }),
      },
    },
  });

  const { data: placeProperties } = useQuery(GetPlaceProperties, {
    skip: category !== FilterCategory.Places,
  });

  const { data: objectMaterials } = useQuery(GetObjectMaterials, {
    skip:
      objectCategory !== FilterObjectCategory.ArtWorks &&
      objectCategory !== FilterObjectCategory.Items,
  });

  const { data: objectTechniques } = useQuery(GetObjectTechniques, {
    skip:
      objectCategory !== FilterObjectCategory.ArtWorks &&
      objectCategory !== FilterObjectCategory.Items,
  });

  const { data: objectItems } = useQuery(GetObjectItems, {
    skip: objectCategory !== FilterObjectCategory.Items,
  });

  const { data: objectPublications } = useQuery(GetObjectPublications, {
    skip: category !== FilterCategory.Objects,
  });

  const onChangeCategory = (e: ChangeEvent<HTMLInputElement>) => {
    setCategory(e.target.id as FilterCategory);
    setFilter([]);
    setObjectCategory(FilterObjectCategory.Publications);
    setPage(1);
  };

  const onChangeObjectCategory = (e: ChangeEvent<HTMLInputElement>) => {
    setObjectCategory(
      e.target.checked
        ? (e.target.id as FilterObjectCategory)
        : FilterObjectCategory.Publications,
    );
    setFilter([]);
    setObjectMaterialFilter([]);
    setObjectTechniqueFilter([]);
    setObjectPublicationsFilter([]);
    setObjectItemsFilter([]);
    setPage(1);
  };

  const onChangeFilter = (e: ChangeEvent<HTMLInputElement>, value: string) => {
    if (e.target.checked) {
      setFilter([...filter, value]);
    } else {
      setFilter(filter.filter((x) => x !== value));
    }
    setPage(1);
  };

  const onChangeObjectPublication = (
    e: ChangeEvent<HTMLInputElement>,
    value: string,
  ) => {
    if (e.target.checked) {
      setObjectPublicationsFilter([...objectPublicationsFilter, value]);
    } else {
      setObjectPublicationsFilter(
        objectPublicationsFilter.filter((x) => x !== value),
      );
    }
    setPage(1);
  };

  const onChangeObjectMaterial = (
    e: ChangeEvent<HTMLInputElement>,
    value: string,
  ) => {
    if (e.target.checked) {
      setObjectMaterialFilter([...objectMaterialFilter, value]);
    } else {
      setObjectMaterialFilter(objectMaterialFilter.filter((x) => x !== value));
    }
    setPage(1);
  };

  const onChangeObjectItem = (
    e: ChangeEvent<HTMLInputElement>,
    value: string,
  ) => {
    if (e.target.checked) {
      setObjectItemsFilter([...objectItemsFilter, value]);
    } else {
      setObjectItemsFilter(objectItemsFilter.filter((x) => x !== value));
    }
    setPage(1);
  };

  const onChangeObjectTechnique = (
    e: ChangeEvent<HTMLInputElement>,
    value: string,
  ) => {
    if (e.target.checked) {
      setObjectTechniqueFilter([...objectTechniqueFilter, value]);
    } else {
      setObjectTechniqueFilter(
        objectTechniqueFilter.filter((x) => x !== value),
      );
    }
    setPage(1);
  };

  let totalCount = 0;
  switch (category) {
    case FilterCategory.People:
      totalCount = people?.count ?? 0;
      break;
    case FilterCategory.Places:
      totalCount = places?.count ?? 0;
      break;
    case FilterCategory.Objects:
      totalCount = objects?.count ?? 0;
      break;
  }
  const totalPages = Math.ceil(totalCount / pageSize);

  const pagination = usePagination({
    total: totalPages,
    page,
    onChange: setPage,
    initialPage: 1,
  });

  return (
    <>
      <Center className={styles.top}>
        <div className={styles.content}>
          <div className={styles.headlineWrapper}>
            <Text
              size={4}
              text={i18n.browse.headline(
                i18n.browse.headlineCategory[category],
              )}
            />
            <Search
              onChange={(value) => {
                setSearchTerm(value);
                setPage(1);
              }}
              value={searchTerm}
            />
          </div>
          <div className={styles.categories}>
            <div className={styles.categoryHeadline}>
              {i18n.browse.categoryHeadline}
            </div>
            <div className={styles.items}>
              <Checkbox
                id={FilterCategory.People}
                checked={category === FilterCategory.People}
                label={i18n.browse.people}
                onChange={onChangeCategory}
              />
              <Checkbox
                id={FilterCategory.Places}
                checked={category === FilterCategory.Places}
                label={i18n.browse.places}
                onChange={onChangeCategory}
              />
              <Checkbox
                id={FilterCategory.Objects}
                checked={category === FilterCategory.Objects}
                label={i18n.browse.objects}
                onChange={onChangeCategory}
              />
              {category === FilterCategory.Objects && (
                <div className={styles.subCategories}>
                  <Checkbox
                    id={FilterObjectCategory.Publications}
                    checked={
                      objectCategory === FilterObjectCategory.Publications
                    }
                    label={i18n.browse.publications}
                    onChange={onChangeObjectCategory}
                  />
                  <Checkbox
                    id={FilterObjectCategory.ArtWorks}
                    checked={objectCategory === FilterObjectCategory.ArtWorks}
                    label={i18n.browse.artworks}
                    onChange={onChangeObjectCategory}
                  />
                  <Checkbox
                    id={FilterObjectCategory.Items}
                    checked={objectCategory === FilterObjectCategory.Items}
                    label={i18n.browse.items}
                    onChange={onChangeObjectCategory}
                  />
                </div>
              )}
            </div>
          </div>
          <div className={styles.dateRangeContainer}>
            <Range
              min={1600}
              max={2050}
              step={1}
              value={years}
              onChange={setYears}
            />
          </div>
        </div>
      </Center>
      {category !== FilterCategory.People &&
        category !== FilterCategory.None && (
          <Center className={styles.properties}>
            <div className={styles.content}>
              {category === FilterCategory.Objects &&
                objectCategory === FilterObjectCategory.Publications && (
                  <div className={styles.itemsWrapper}>
                    <div className={styles.items}>
                      {objectPublications?.items?.map((x) => {
                        return (
                          <Checkbox
                            id={x.id}
                            key={x.id}
                            label={x.name ?? ""}
                            checked={objectPublicationsFilter.includes(x.id)}
                            onChange={(e) => onChangeObjectPublication(e, x.id)}
                          />
                        );
                      })}
                    </div>
                  </div>
                )}
              {category === FilterCategory.Objects &&
                objectCategory === FilterObjectCategory.Items && (
                  <div className={styles.itemsWrapper}>
                    <div className={styles.items}>
                      {objectItems?.items?.map((x) => {
                        return (
                          <Checkbox
                            id={x.id}
                            key={x.id}
                            label={x.name ?? ""}
                            checked={objectItemsFilter.includes(x.id)}
                            onChange={(e) => onChangeObjectItem(e, x.id)}
                          />
                        );
                      })}
                    </div>
                    <div className={styles.items}>
                      {objectMaterials?.items?.map((x) => {
                        return (
                          <Checkbox
                            id={x.id}
                            key={x.id}
                            label={x.name ?? ""}
                            checked={objectMaterialFilter.includes(x.id)}
                            onChange={(e) => onChangeObjectMaterial(e, x.id)}
                          />
                        );
                      })}
                    </div>
                    <div className={styles.items}>
                      {objectTechniques?.items?.map((x) => {
                        return (
                          <Checkbox
                            id={x.id}
                            key={x.id}
                            label={x.name ?? ""}
                            checked={objectTechniqueFilter.includes(x.id)}
                            onChange={(e) => onChangeObjectTechnique(e, x.id)}
                          />
                        );
                      })}
                    </div>
                  </div>
                )}
              {category === FilterCategory.Objects &&
                objectCategory === FilterObjectCategory.ArtWorks && (
                  <div className={styles.itemsWrapper}>
                    <div className={styles.items}>
                      {objectMaterials?.items?.map((x) => {
                        return (
                          <Checkbox
                            id={x.id}
                            key={x.id}
                            label={x.name ?? ""}
                            checked={objectMaterialFilter.includes(x.id)}
                            onChange={(e) => onChangeObjectMaterial(e, x.id)}
                          />
                        );
                      })}
                    </div>
                    <div className={styles.items}>
                      {objectTechniques?.items?.map((x) => {
                        return (
                          <Checkbox
                            id={x.id}
                            key={x.id}
                            label={x.name ?? ""}
                            checked={objectTechniqueFilter.includes(x.id)}
                            onChange={(e) => onChangeObjectTechnique(e, x.id)}
                          />
                        );
                      })}
                    </div>
                  </div>
                )}
              {category === FilterCategory.Places && (
                <div className={styles.itemsWrapper}>
                  <div className={styles.items}>
                    {placeProperties?.items?.map((x) => {
                      return (
                        <Checkbox
                          id={x.id}
                          key={x.id}
                          label={x.name ?? ""}
                          checked={filter.includes(x.id)}
                          onChange={(e) => onChangeFilter(e, x.id)}
                        />
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </Center>
        )}
      <Center className={styles.results}>
        <div className={styles.content}>
          {category !== FilterCategory.None && (
            <div className={styles.views}>
              <Button
                layout={ButtonLayout.Unstyled}
                buttonProps={{
                  title: i18n.browse.timeline,
                  onClick: () => setResultView(ResultView.Timeline),
                  className: clsx(styles.view, {
                    [styles.active]: resultView === ResultView.Timeline,
                  }),
                }}
              >
                {i18n.browse.timeline}
              </Button>
              <Button
                layout={ButtonLayout.Unstyled}
                buttonProps={{
                  title: i18n.browse.list,
                  onClick: () => setResultView(ResultView.List),
                  className: clsx(styles.view, {
                    [styles.active]: resultView === ResultView.List,
                  }),
                }}
              >
                {i18n.browse.list}
              </Button>
              <Button
                layout={ButtonLayout.Unstyled}
                buttonProps={{
                  title: i18n.browse.image,
                  onClick: () => setResultView(ResultView.Image),
                  className: clsx(styles.view, {
                    [styles.active]: resultView === ResultView.Image,
                  }),
                }}
              >
                {i18n.browse.image}
              </Button>
            </div>
          )}
          {resultView === ResultView.List &&
            category === FilterCategory.People && (
              <List
                category={FilterCategory.People}
                data={
                  people?.items?.map((x) => {
                    return {
                      id: x.id,
                      row1: x.dateOfBirth
                        ? format(
                            parseDate(x.dateOfBirth as string),
                            "dd.MM.yyyy",
                          )
                        : "",
                      row2: `${x.givenName ?? ""} ${x.familyName ?? ""}`,
                      row3: x.sex ? i18n.browse.sex[x.sex] : "",
                      row4: x.placeOfBirth?.displayName ?? "",
                      row5: x.dateOfDeath
                        ? format(
                            parseDate(x.dateOfDeath as string),
                            "dd.MM.yyyy",
                          )
                        : "",
                    };
                  }) ?? []
                }
              />
            )}
          {resultView === ResultView.List &&
            category === FilterCategory.Places && (
              <List
                category={FilterCategory.Places}
                data={
                  places?.items?.map((x) => {
                    return {
                      id: x.id,
                      row1: x.area?.name ?? "",
                      row2: x.name ?? "",
                      row3: x.nativeNames ?? "",
                      row4: x.typen?.map((x) => x.name).join(", ") ?? "",
                      row5: x.address ?? "",
                    };
                  }) ?? []
                }
              />
            )}
          {resultView === ResultView.List &&
            category === FilterCategory.Objects && (
              <List
                category={FilterCategory.Objects}
                data={
                  objects?.items?.map((x) => {
                    return {
                      id: x.id,
                      row1: x.periodOfOrigin?.displayName ?? "",
                      row2: x.title ?? "",
                      row3:
                        x.authorPerson
                          ?.map(
                            (author) =>
                              `${author.familyName} ${author.givenName}`,
                          )
                          .join(", ") ?? "",
                      row4: x.publisher ?? "",
                      row5: x.pages ?? "",
                    };
                  }) ?? []
                }
              />
            )}
          {resultView === ResultView.Image &&
            category === FilterCategory.Objects && (
              <Image
                category={FilterCategory.Objects}
                data={
                  objects?.items?.map((x) => {
                    return {
                      id: x.id,
                      image:
                        (x.images?.length ?? 0) > 0
                          ? (x.images?.[0]?.bild?.url ?? "")
                          : (x.imageObjects?.[0]?.images?.[0]?.bild?.url ?? ""),
                      title: x.title ?? "",
                      subTitle:
                        x.authorPerson
                          ?.map(
                            (author) =>
                              `${author.givenName} ${author.familyName}`,
                          )
                          .join(", ") ?? "",
                    };
                  }) ?? []
                }
              />
            )}
          {resultView === ResultView.Image &&
            category === FilterCategory.People && (
              <Image
                category={FilterCategory.People}
                data={
                  people?.items?.map((x) => {
                    return {
                      id: x.id,
                      image:
                        [
                          ...(x.images?.flatMap(
                            (x) =>
                              x.images?.map((y) => ({
                                src: y.bild?.url ?? "",
                                title: y.objekte?.[0]?.title ?? "",
                              })) ?? [],
                          ) ?? []),
                          ...(x.images?.flatMap(
                            (x) =>
                              x.imageObjects?.flatMap(
                                (y) =>
                                  y.images?.map((z) => ({
                                    src: z.bild?.url ?? "",
                                    title: y.title ?? "",
                                  })) ?? [],
                              ) ?? [],
                          ) ?? []),
                        ][0]?.src ?? "",
                      title: `${x.givenName} ${x.familyName}`,
                      subTitle: `${
                        x.dateOfBirth
                          ? format(parseDate(x.dateOfBirth), "yyyy")
                          : ""
                      } ${x.dateOfDeath ? ` — ${format(parseDate(x.dateOfDeath), "yyyy")}` : ""}`,
                    };
                  }) ?? []
                }
              />
            )}
          {resultView === ResultView.Image &&
            category === FilterCategory.Places && (
              <Image
                category={FilterCategory.Places}
                data={
                  places?.items?.map((x) => {
                    return {
                      id: x.id,
                      image:
                        x.images?.images?.map((x) => x.bild?.url ?? "")[0] ??
                        "",
                      title: x.name ?? "",
                      subTitle: x.area?.name ?? "",
                    };
                  }) ?? []
                }
              />
            )}
          {resultView === ResultView.Timeline &&
            category === FilterCategory.Objects && (
              <Timeline
                category={FilterCategory.Objects}
                data={
                  objects?.items?.map((x) => {
                    return {
                      id: x.id,
                      image:
                        (x.images?.length ?? 0) > 0
                          ? (x.images?.[0]?.bild?.url ?? "")
                          : (x.imageObjects?.[0]?.images?.[0]?.bild?.url ?? ""),
                      title: x.title ?? "",
                      subTitle: `${
                        x.periodOfOrigin?.zeitspanne?.von
                          ? format(
                              parseDate(x.periodOfOrigin.zeitspanne.von),
                              "yyyy",
                            )
                          : ""
                      } ${x.periodOfOrigin?.zeitspanne?.bis ? ` — ${format(parseDate(x.periodOfOrigin.zeitspanne.bis), "yyyy")}` : ""}`,
                    };
                  }) ?? []
                }
              />
            )}
          {resultView === ResultView.Timeline &&
            category === FilterCategory.People && (
              <Timeline
                category={FilterCategory.People}
                data={
                  people?.items?.map((x) => {
                    return {
                      id: x.id,
                      image:
                        x.images?.map(
                          (x) => x.images?.[0]?.bild?.url ?? "",
                        )[0] ?? "",
                      title: `${x.givenName} ${x.familyName}`,
                      subTitle: `${
                        x.dateOfBirth
                          ? format(parseDate(x.dateOfBirth), "yyyy")
                          : ""
                      } ${x.dateOfDeath ? ` — ${format(parseDate(x.dateOfDeath), "yyyy")}` : ""}`,
                    };
                  }) ?? []
                }
              />
            )}
          {resultView === ResultView.Timeline &&
            category === FilterCategory.Places && (
              <Timeline
                category={FilterCategory.Places}
                data={
                  places?.items?.map((x) => {
                    return {
                      id: x.id,
                      image:
                        x.images?.images?.map((x) => x.bild?.url ?? "")[0] ??
                        "",
                      title: x.name ?? "",
                      subTitle: x.area?.name ?? "",
                    };
                  }) ?? []
                }
              />
            )}
          {category === FilterCategory.None && (
            <div className={styles.noResults}>{i18n.browse.noResults}</div>
          )}
          {(placesLoading || peoplesLoading || objectsLoading) && <Loader />}
          {category === FilterCategory.Objects &&
            objects?.items?.length === 0 &&
            !objectsLoading && (
              <div className={styles.noResults}>{i18n.browse.noResults}</div>
            )}
          {category === FilterCategory.People &&
            people?.items?.length === 0 &&
            !peoplesLoading && (
              <div className={styles.noResults}>{i18n.browse.noResults}</div>
            )}
          {category === FilterCategory.Places &&
            places?.items?.length === 0 &&
            !placesLoading && (
              <div className={styles.noResults}>{i18n.browse.noResults}</div>
            )}
          <Pagination
            range={pagination.range}
            currentPage={page}
            onChange={(page) => setPage(page)}
          />
        </div>
      </Center>
    </>
  );
};

export default Browse;
